const LOCAL_PHONE_LENGTH = 10;
const INTERNATIONAL_PHONE_LENGTH = 11;

function removeNonDigits(rawValue: string): string {
  return rawValue.replace(/\D/g, "");
}

function limitToUsLengths(digitsOnly: string): string {
  if (digitsOnly.startsWith("1")) {
    return digitsOnly.slice(0, INTERNATIONAL_PHONE_LENGTH);
  }

  return digitsOnly.slice(0, LOCAL_PHONE_LENGTH);
}

/**
 * Formats a raw phone input value while the user types. The logic keeps the
 * number within standard US lengths and progressively applies parentheses and
 * hyphen separators so the field remains easy to read.
 */
export function formatPhoneInputValue(rawValue: string): string {
  const digitsOnly = limitToUsLengths(removeNonDigits(rawValue));

  if (digitsOnly.length === 0) {
    return "";
  }

  if (digitsOnly === "1") {
    return "+1";
  }

  if (digitsOnly.length <= 3) {
    return `(${digitsOnly}`;
  }

  if (digitsOnly.length <= 6) {
    const areaCode = digitsOnly.slice(0, 3);
    const prefix = digitsOnly.slice(3);

    return `(${areaCode}) ${prefix}`;
  }

  if (
    digitsOnly.length <= LOCAL_PHONE_LENGTH ||
    (digitsOnly.length < INTERNATIONAL_PHONE_LENGTH && digitsOnly.startsWith("1"))
  ) {
    const areaCode = digitsOnly.slice(0, 3);
    const prefix = digitsOnly.slice(3, 6);
    const lineNumber = digitsOnly.slice(6);

    return `(${areaCode}) ${prefix}-${lineNumber}`;
  }

  const areaCode = digitsOnly.slice(1, 4);
  const prefix = digitsOnly.slice(4, 7);
  const lineNumber = digitsOnly.slice(7, INTERNATIONAL_PHONE_LENGTH);

  return `+1 (${areaCode}) ${prefix}-${lineNumber}`;
}

/**
 * Removes any non-digit characters from a phone value. Useful before sending
 * the number to an API that expects only digits.
 */
export function stripPhoneFormatting(rawValue: string): string {
  return removeNonDigits(rawValue);
}

/**
 * Converts any phone format to E.164 format for API submission
 * Examples:
 *   (213) 131-2312 -> +12131312312
 *   213-131-2312 -> +12131312312
 *   2131312312 -> +12131312312
 *   +1 (213) 131-2312 -> +12131312312
 * 
 * E.164 format: +[country code][number]
 * For US numbers: +1 followed by 10 digits (no spaces, no special chars)
 */
export function toE164Format(rawValue: string | null | undefined): string {
  if (!rawValue) return "";
  
  // Remove all non-digit characters
  const digitsOnly = removeNonDigits(rawValue);
  
  // If empty, return empty
  if (digitsOnly.length === 0) {
    return "";
  }
  
  // If it starts with 1 and has 11 digits (1 + 10), it's already complete
  if (digitsOnly.startsWith("1") && digitsOnly.length === 11) {
    return `+${digitsOnly}`;
  }
  
  // If it's 10 digits, assume US and add +1 prefix
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }
  
  // If it has 11 digits but doesn't start with 1, might be international
  if (digitsOnly.length === 11) {
    return `+${digitsOnly}`;
  }
  
  // For longer international numbers, just add + prefix
  if (digitsOnly.length > 11) {
    return `+${digitsOnly}`;
  }
  
  // For shorter numbers (incomplete), add +1 prefix anyway for validation
  return `+1${digitsOnly}`;
}
